/**
 * HarmonizePanel.tsx (GRE-122 Phase C) - DISABLED
 *
 * Harmonization panel is disabled after melody-first workflow removal.
 * Melody input now comes from uploaded MIDI/MusicXML via Analyze panel.
 */

import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { Chord } from '@/types/chord';
import styles from './WhyThisPanel.module.css';
import panelStyles from './HarmonizePanel.module.css';

/**
 * Props for HarmonizePanel
 */
interface HarmonizePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyHarmonization: (chords: Chord[]) => void;
}

/**
 * HarmonizePanel Component (Disabled)
 */
export const HarmonizePanel: React.FC<HarmonizePanelProps> = ({
  isOpen,
  onClose,
}) => {
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const handleGenerate = useCallback(async () => {
    setError('Harmonization requires imported MIDI or MusicXML. Please import a file using the Analyze panel first.');
  }, [setError]);

  const content = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay backdrop */}
          <motion.div
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Panel */}
          <motion.div
            className={styles.panel}
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            role="complementary"
            aria-modal="false"
            aria-labelledby="harmonize-title"
          >
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.headerContent}>
                <h2 id="harmonize-title" className={styles.title}>
                  Harmonize Melody
                </h2>
                <p className={styles.subtitle}>
                  (Disabled - Import MIDI/MusicXML to use)
                </p>
              </div>
              <button
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Close panel"
                title="Close (Esc)"
              >
                ✕
              </button>
            </div>

            {/* Main content */}
            <div className={styles.content}>
              {/* Empty state */}
              <div className={styles.emptyState}>
                <p>Harmonization requires imported MIDI or MusicXML.</p>
                <p>Please use the Analyze panel to import a file first.</p>
              </div>

              {/* Error message if any */}
              {error && (
                <div className={styles.errorContainer}>
                  <div className={styles.errorIcon}>⚠</div>
                  <p className={styles.errorMessage}>{error}</p>
                </div>
              )}

              {/* Action Buttons */}
              <section className={styles.section}>
                <div className={panelStyles.buttonGroup}>
                  <button
                    className={`${styles.playButton} ${styles.primary}`}
                    onClick={handleGenerate}
                    disabled={true}
                  >
                    Generate Harmonization
                  </button>
                </div>
              </section>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};

export default HarmonizePanel;
